'use client';

import { useEffect, useRef } from 'react';

/**
 * ConsolidationPipeline
 * Animated "many fragmented tools → one NetSuite" panel (no title content —
 * the surrounding slice/section supplies its own eyelash/title/description).
 *
 * Usage:  <ConsolidationPipeline />
 */

const SOURCES = [
  { label: 'Excel financial models', icon: 'sheet' },
  { label: 'Standalone inventory sheet', icon: 'box' },
  { label: 'CRM exported by email', icon: 'mail' },
  { label: 'Disconnected payment logs', icon: 'card' },
];

function Icon({ name }) {
  const common = {
    viewBox: '0 0 24 24',
    fill: 'none',
    stroke: 'currentColor',
    strokeWidth: 1.8,
    strokeLinecap: 'round',
    strokeLinejoin: 'round',
  };
  switch (name) {
    case 'sheet':
      return (
        <svg {...common}>
          <rect x="3.5" y="3.5" width="17" height="17" rx="2" />
          <path d="M3.5 9h17M3.5 14.5h17M9 3.5v17M14.5 3.5v17" />
        </svg>
      );
    case 'box':
      return (
        <svg {...common}>
          <path d="M21 8.5 12 3.5 3 8.5v7L12 20.5l9-5z" />
          <path d="M3 8.5 12 13.5l9-5M12 13.5V20.5" />
        </svg>
      );
    case 'mail':
      return (
        <svg {...common}>
          <rect x="3" y="5" width="18" height="14" rx="2" />
          <path d="m3.5 6.5 8.5 6 8.5-6" />
        </svg>
      );
    case 'card':
      return (
        <svg {...common}>
          <rect x="3" y="5" width="18" height="14" rx="2" />
          <path d="M3 9.5h18M6.5 14.5h4" />
        </svg>
      );
    default:
      return null;
  }
}

export default function ConsolidationPipeline({
  productName = 'One real-time source of truth',
  logoSrc = '/netsuite-logo-dark-bg.png',
}) {
  const rootRef = useRef(null);

  useEffect(() => {
    const root = rootRef.current;
    if (!root) return;

    const ns = root.querySelector('.ns');

    // Both ends of the desktop wires are measured, not hardcoded: each wire
    // leaves its source card at the card's actual vertical center and lands on
    // the NetSuite panel's actual left-edge center. Row heights and the middle
    // gap both change with container width, so viewBox-unit constants drift
    // apart from the real elements at compressed sizes.
    const wiresH = root.querySelector('.wires-h');
    const srcEls = [...root.querySelectorAll('.left .src')];
    // Second-control-point x, as a fraction of the wire span (per-wire flavor
    // kept from the original 210/220-of-420 authoring).
    const CP2X_FRAC = [0.5, 0.524, 0.524, 0.5];

    const syncWireStarts = () => {
      if (!wiresH || wiresH.offsetParent === null || srcEls.length === 0) return;
      const svgRect = wiresH.getBoundingClientRect();
      if (svgRect.height === 0 || svgRect.width === 0) return;
      // Draw in a 1:1 pixel coordinate space (viewBox = rendered size) so
      // stroke widths and packet dots keep their intended size no matter how
      // narrow the middle gap gets — a stretched fixed viewBox scales them
      // down to hairlines at compressed widths.
      wiresH.setAttribute('viewBox', `0 0 ${svgRect.width} ${svgRect.height}`);
      const nsRect = ns.getBoundingClientRect();
      // End on the panel's left-edge center, overshooting a few px so the line
      // tucks under the panel (svg overflows visibly, panel is z-above)
      // instead of stopping short of it.
      const endX = nsRect.left - svgRect.left + 8;
      const endY = nsRect.top + nsRect.height / 2 - svgRect.top;
      const paths = [...wiresH.querySelectorAll('path')];
      srcEls.forEach((src, i) => {
        const path = paths[i];
        if (!path) return;
        const srcRect = src.getBoundingClientRect();
        const y = srcRect.top + srcRect.height / 2 - svgRect.top;
        const cp2x = (CP2X_FRAC[i] ?? 0.5) * endX;
        path.setAttribute('d', `M0 ${y} C${0.4 * endX} ${y}, ${cp2x} ${endY}, ${endX} ${endY}`);
      });
    };

    syncWireStarts();

    const DUR = 1600,
      STAGGER = 260,
      CYCLE = DUR * 1.6;

    const rigs = [...root.querySelectorAll('.wires')].map((svg) => {
      const paths = [...svg.querySelectorAll('path')];
      return {
        svg,
        paths,
        pkts: [...svg.querySelectorAll('.pkt')],
        lens: paths.map((p) => p.getTotalLength()),
      };
    });

    const resync = () => {
      syncWireStarts();
      rigs.forEach((rig) => {
        rig.lens = rig.paths.map((p) => p.getTotalLength());
      });
    };
    window.addEventListener('resize', resync);
    // Container width can change without a window resize (gallery/embed
    // layouts, font load reflow) — re-measure on any element resize too.
    let ro;
    if (typeof ResizeObserver !== 'undefined') {
      ro = new ResizeObserver(resync);
      ro.observe(root);
    }

    const t0 = Date.now();
    const step = () => {
      const el = Date.now() - t0;
      rigs.forEach((rig) => {
        const visible = rig.svg.offsetParent !== null;
        rig.pkts.forEach((pk, i) => {
          const local =
            (((el - i * STAGGER) % CYCLE) + CYCLE) % CYCLE / DUR;
          if (local > 1) {
            pk.style.opacity = 0;
            return;
          }
          pk.style.opacity =
            local < 0.12
              ? local / 0.12
              : local > 0.88
              ? (1 - local) / 0.12
              : 1;
          const pt = rig.paths[i].getPointAtLength(local * rig.lens[i]);
          pk.setAttribute('cx', pt.x);
          pk.setAttribute('cy', pt.y);
          if (visible && local > 0.9 && !pk._hit) {
            pk._hit = true;
            ns.classList.remove('pulse');
            void ns.offsetWidth;
            ns.classList.add('pulse');
          }
          if (local < 0.5) pk._hit = false;
        });
      });
    };

    const id = setInterval(step, 1000 / 30);
    step();
    return () => {
      clearInterval(id);
      window.removeEventListener('resize', resync);
      ro?.disconnect();
    };
  }, []);

  return (
    <section className="cp-section" ref={rootRef} data-screen-label="Live pipeline">
      <div className="panel">
        <div className="stage">
          <div className="left">
            {SOURCES.map((s) => (
              <div className="src" key={s.label}>
                <span className="ic">
                  <Icon name={s.icon} />
                </span>
                {s.label}
              </div>
            ))}
          </div>

          {/* Desktop wires (left → right) */}
          <svg className="wires wires-h" viewBox="0 0 420 300" preserveAspectRatio="none">
            <path d="M0 46  C170 46, 210 150, 420 150" />
            <path d="M0 112 C170 112, 220 150, 420 150" />
            <path d="M0 178 C170 178, 220 150, 420 150" />
            <path d="M0 244 C170 244, 210 150, 420 150" />
            <circle className="pkt" r="5.5" cx="0" cy="46" />
            <circle className="pkt" r="5.5" cx="0" cy="112" />
            <circle className="pkt" r="5.5" cx="0" cy="178" />
            <circle className="pkt" r="5.5" cx="0" cy="244" />
          </svg>

          {/* Mobile wires (top → down) */}
          <svg className="wires wires-v" viewBox="0 0 300 100" preserveAspectRatio="none">
            <path d="M55 0  C55 55, 150 55, 150 100" />
            <path d="M118 0 C118 50, 150 55, 150 100" />
            <path d="M182 0 C182 50, 150 55, 150 100" />
            <path d="M245 0 C245 55, 150 55, 150 100" />
            <circle className="pkt" r="3.5" cx="55" cy="0" />
            <circle className="pkt" r="3.5" cx="118" cy="0" />
            <circle className="pkt" r="3.5" cx="182" cy="0" />
            <circle className="pkt" r="3.5" cx="245" cy="0" />
          </svg>

          <div className="right">
            <div className="ns">
              {/* Using a plain <img> to avoid next/image config. Swap for next/image if you prefer. */}
              {/* eslint-disable-next-line @next/next/no-img-element */}
              <img src={logoSrc} alt="Oracle NetSuite" />
              <span className="tag">{productName}</span>
            </div>
          </div>
        </div>
      </div>

      <style jsx>{`
        .cp-section {
          --ink: #14263b;
          --muted: #8493a1;
          --line: #e5e8ec;
          --panel: linear-gradient(135deg, #ecfbf9 0%, #d0f3ee 100%);
          --paper: #ffffff;
          --ns-1: #04445c;
          --ns-2: #176a6f;
          --wire: #d2d9e0;
          width: 100%;
          background: linear-gradient(135deg, #ecfbf9 0%, #d0f3ee 100%);
          border-radius: 20px;
          color: var(--ink);
        }
        .panel {
          position: relative;
          background: var(--panel);
          border-radius: 20px;
          padding: 15px 52px;
          overflow: hidden;
        }
        .stage {
          position: relative;
          display: flex;
          align-items: center;
          min-height: 0;
        }
        .left {
          width: 300px;
          flex: none;
          display: flex;
          flex-direction: column;
          gap: 13px;
          z-index: 2;
        }
        .src {
          display: inline-flex;
          align-items: center;
          gap: 10px;
          padding: 11px 16px;
          background: var(--paper);
          border-radius: 10px;
          box-shadow: 0 8px 22px -12px rgba(20, 38, 59, 0.28);
          font-size: 13.5px;
          font-weight: 600;
          color: var(--ink);
          white-space: nowrap;
        }
        .src .ic {
          width: 26px;
          height: 26px;
          border-radius: 7px;
          flex: none;
          background: #eef0f3;
          color: #000000;
          display: grid;
          place-items: center;
        }
        .src .ic :global(svg) {
          width: 15px;
          height: 15px;
        }
        .wires-h {
          flex: 1 1 0%;
          /* The viewBox gives the svg an intrinsic aspect ratio; combined with
             the stretched height, min-width:auto would transfer that into a
             ~1.4x-stage-height minimum width that blows the row out and pushes
             .right off the panel. Zero the floor so flex sizes the gap. */
          min-width: 0;
          align-self: stretch;
          width: 100%;
          height: 100%;
          margin-left: -12px;
          z-index: 1;
          /* Wire endpoints are measured against the NetSuite panel and drawn
             slightly past the viewBox so they tuck under it — don't clip. */
          overflow: visible;
        }
        .wires :global(path) {
          fill: none;
          stroke: var(--wire);
          stroke-width: 2;
        }
        /* Desktop wires draw in 1:1 pixel space (viewBox synced to rendered
           size), so this is a true px value — keep them clearly visible. */
        .wires-h :global(path) {
          stroke-width: 3;
        }
        .wires :global(.pkt) {
          fill: var(--ns-2);
        }
        .wires-v {
          display: none;
        }
        .right {
          width: 320px;
          flex: none;
          z-index: 2;
        }
        .ns {
          background: linear-gradient(115deg, var(--ns-1) 0%, var(--ns-2) 100%);
          border-radius: 14px;
          padding: 30px 32px;
          box-shadow: 0 26px 46px -22px rgba(4, 68, 92, 0.6);
          transition: box-shadow 0.6s;
        }
        .ns :global(img) {
          display: block;
          width: 210px;
          height: auto;
        }
        .ns .tag {
          display: block;
          font-size: 13px;
          font-weight: 400;
          color: rgba(255, 255, 255, 0.85);
          margin-top: 12px;
        }
        .ns.pulse {
          animation: bpulse 0.5s ease;
        }
        @keyframes bpulse {
          50% {
            transform: scale(1.035);
          }
        }
        @media (prefers-reduced-motion: reduce) {
          .ns.pulse {
            animation: none;
          }
        }
        @media (max-width: 640px) {
          .panel { padding: 16px 18px 20px; }
          .stage { flex-direction: column; align-items: stretch; min-height: 0; gap: 0; }
          .left { width: 100%; display: grid; grid-template-columns: 1fr 1fr; gap: 10px; }
          .src { width: 100%; font-size: 12.5px; padding: 12px 13px; white-space: normal; line-height: 1.25; }
          .wires-h { display: none; }
          .wires-v { display: block; position: static; width: 100%; height: 90px; z-index: 1; margin-top: 4px; }
          .right { width: 100%; margin-left: 0; z-index: 2; }
          .ns { text-align: center; padding: 26px 24px; }
          .ns :global(img) { width: 180px; margin: 0 auto; }
          .ns .tag { margin-top: 10px; }
        }
      `}</style>
    </section>
  );
}
